import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Sparkles, Home, Settings,
  HelpCircle, LogOut, Menu, DatabaseZap, Activity,
  Zap, ChevronLeft, PanelLeftOpen, TrendingUp, MessageCircle // Added collapse/expand icons and Market icon
} from 'lucide-react';
import { createTranslator } from '../../utils/detectLanguage';
import { desktopNavigationPtBR } from '../mappings/desktopNavigation.pt-br';
import { desktopNavigationES } from '../mappings/desktopNavigation.es';

const DesktopNavigation = ({
  user,
  isExpanded,
  setIsExpanded,
  isFullyCollapsed,
  setIsFullyCollapsed,
  // showBackToChat,
  showUserMenu,
  setShowUserMenu,
  openLogoutModal,
  dropdownRef
}) => {
  const navigate = useNavigate();

  const handleNavigation = (path) => {
    navigate(path);
    setShowUserMenu(false);
  };

  // Helper function to get translated text
  const tLocal = createTranslator({
    'pt-br': desktopNavigationPtBR,
    'es': desktopNavigationES
  });

  return (
    <>
      {/* Main Navigation Sidebar */}
      <motion.div
        ref={dropdownRef}
        animate={{
          width: isFullyCollapsed ? 0 : (isExpanded ? 240 : 72),
          opacity: isFullyCollapsed ? 0 : 1
        }}
        transition={{ duration: 0.15, ease: [0.4, 0.0, 0.2, 1] }}
        className="hidden md:block fixed top-0 left-0 h-screen bg-slate-50 shadow-lg z-50 overflow-hidden"
        style={{ display: isFullyCollapsed ? 'none' : undefined }}
      >
        <div className="flex flex-col h-full">
          {/* Hamburger Menu */}
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="w-full h-14 flex items-center px-6 text-slate-700 hover:bg-slate-100 transition-colors"
          >
            <Menu size={22} className="flex-shrink-0 text-black-600" />
            <motion.span
              initial={false}
              animate={{ opacity: isExpanded ? 1 : 0 }}
              transition={{ duration: 0.1 }}
              className="ml-4 text-lg font-sans font-bold whitespace-nowrap"
            >
              PickStar
            </motion.span>
          </button>

        <div className="flex-1 px-3 pt-3 space-y-0.5">
          {/* Full Collapse Button - Always visible */}
          <button
            onClick={() => {
              setIsFullyCollapsed(true);
              setIsExpanded(false);
            }}
            className="w-full h-11 flex items-center px-3 rounded-lg text-slate-500 hover:bg-red-50 hover:text-red-600 transition-colors mb-2"
            title="Hide sidebar completely"
          >
            <ChevronLeft size={22} className="flex-shrink-0" />
            <motion.span
              initial={false}
              animate={{ opacity: isExpanded ? 1 : 0 }}
              transition={{ duration: 0.1 }}
              className="ml-4 text-sm font-medium whitespace-nowrap"
            >
              {tLocal("Hide Sidebar")}
            </motion.span>
          </button>

          {/* Divider */}
          <div className="h-px bg-slate-200 my-2"></div>

          {/* 1. Home */}
          <button
            onClick={() => handleNavigation('/')}
            className="w-full h-11 flex items-center px-3 rounded-lg text-violet-600 hover:bg-slate-50 transition-colors"
          >
            <Home size={22} className="flex-shrink-0" />
            <motion.span
              initial={false}
              animate={{ opacity: isExpanded ? 1 : 0 }}
              transition={{ duration: 0.1 }}
              className="ml-4 text-sm font-medium whitespace-nowrap"
            >
              {tLocal("Home")}
            </motion.span>
          </button>

          {/* 2. Chat */}
          <button
            onClick={() => handleNavigation('/chat')}
            className="w-full h-11 flex items-center px-3 rounded-lg text-violet-600 hover:bg-slate-50 transition-colors"
          >
            <MessageCircle size={22} className="flex-shrink-0" />
            <motion.span
              initial={false}
              animate={{ opacity: isExpanded ? 1 : 0 }}
              transition={{ duration: 0.1 }}
              className="ml-4 text-sm font-medium whitespace-nowrap"
            >
              {tLocal("Chat")}
            </motion.span>
          </button>

          {/* 3. Market */}
          <button
            onClick={() => handleNavigation('/market')}
            className="w-full h-11 flex items-center px-3 rounded-lg text-violet-600 hover:bg-violet-50 transition-colors"
          >
            <TrendingUp size={22} className="flex-shrink-0 text-violet-600" />
            <motion.span
              initial={false}
              animate={{ opacity: isExpanded ? 1 : 0 }}
              transition={{ duration: 0.1 }}
              className="ml-4 text-sm font-medium whitespace-nowrap"
            >
              {tLocal("AI Exchange")}
            </motion.span>
          </button>

          {/* 5. Upgrade */}
          <button
            onClick={() => handleNavigation('/upgrade')}
            className="w-full h-11 flex items-center px-3 rounded-lg text-emerald-500 hover:bg-slate-50 transition-colors"
          >
            <Sparkles size={22} className="flex-shrink-0" />
            <motion.span
              initial={false}
              animate={{ opacity: isExpanded ? 1 : 0 }}
              transition={{ duration: 0.1 }}
              className="ml-4 text-sm font-medium whitespace-nowrap"
            >
              {tLocal("Upgrade")}
            </motion.span>
          </button>

          {/* 5. Settings */}
          <button
            onClick={() => handleNavigation('/settings')}
            className="w-full h-11 flex items-center px-3 rounded-lg text-violet-600 hover:bg-slate-50 transition-colors"
          >
            <Settings size={22} className="flex-shrink-0" />
            <motion.span
              initial={false}
              animate={{ opacity: isExpanded ? 1 : 0 }}
              transition={{ duration: 0.1 }}
              className="ml-4 text-sm font-medium whitespace-nowrap"
            >
              {tLocal("Settings")}
            </motion.span>
          </button>

          {/* 6. Chat Archive */}
          <button
            onClick={() => handleNavigation('/chat-history')}
            className="w-full h-11 flex items-center px-3 rounded-lg text-violet-600 hover:bg-violet-50 transition-colors"
          >
            <DatabaseZap size={22} className="flex-shrink-0 text-violet-600" />
            <motion.span
              initial={false}
              animate={{ opacity: isExpanded ? 1 : 0 }}
              transition={{ duration: 0.1 }}
              className="ml-4 text-sm font-medium whitespace-nowrap"
            >
              {tLocal("Chat Archive")}
            </motion.span>
          </button>

          {/* 7. About Us */}
          <button
            onClick={() => handleNavigation('/about-us')}
            className="w-full h-11 flex items-center px-3 rounded-lg text-violet-600 hover:bg-slate-50 transition-colors"
          >
            <Zap size={22} className="flex-shrink-0" />
            <motion.span
              initial={false}
              animate={{ opacity: isExpanded ? 1 : 0 }}
              transition={{ duration: 0.1 }}
              className="ml-4 text-sm font-medium whitespace-nowrap"
            >
              {tLocal("About Us")}
            </motion.span>
          </button>

          {/* Help - keeping this for completeness */}
          <button
            onClick={() => handleNavigation('/help')}
            className="w-full h-11 flex items-center px-3 rounded-lg text-violet-600 hover:bg-slate-50 transition-colors"
          >
            <HelpCircle size={22} className="flex-shrink-0" />
            <motion.span
              initial={false}
              animate={{ opacity: isExpanded ? 1 : 0 }}
              transition={{ duration: 0.1 }}
              className="ml-4 text-sm font-medium whitespace-nowrap"
            >
              {tLocal("Help")}
            </motion.span>
          </button>

          {/* 8. Sign Out */}
          <button
            onClick={openLogoutModal}
            className="w-full h-11 flex items-center px-3 rounded-lg text-violet-600 hover:bg-red-50 transition-colors mt-2"
          >
            <LogOut size={22} className="flex-shrink-0 text-violet-600" />
            <motion.span
              initial={false}
              animate={{ opacity: isExpanded ? 1 : 0 }}
              transition={{ duration: 0.1 }}
              className="ml-4 text-sm font-medium whitespace-nowrap"
            >
              {tLocal("Sign Out")}
            </motion.span>
          </button>
        </div>

          {/* User Menu */}
          <div className="p-3 mt-auto">
            <button
              onClick={() => setShowUserMenu(!showUserMenu)}
              className={`flex items-center ${
                isExpanded ? 'w-full px-3' : 'w-10 mx-auto'
              } h-10 bg-amber-50 hover:bg-amber-100 text-slate-700
                rounded-lg transition-all duration-150`}
            >
              <div className={`${!isExpanded && 'w-full'} flex justify-center text-base font-semibold`}>
                {user?.displayName?.charAt(0).toUpperCase()}
              </div>
              <motion.span
                initial={false}
                animate={{ opacity: isExpanded ? 1 : 0 }}
                transition={{ duration: 0.1 }}
                className="ml-3 text-sm font-medium truncate whitespace-nowrap"
              >
                {user?.displayName}
              </motion.span>
            </button>

            <AnimatePresence>
              {showUserMenu && (
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 8 }}
                  transition={{ duration: 0.1 }}
                  className={`hidden md:block absolute bottom-[72px] ${
                    isExpanded ? 'left-3 right-3' : 'left-16 w-48'
                  } bg-white rounded-lg shadow-lg py-2 border border-slate-200`}
                >
                  <div className="px-4 py-2 border-b border-slate-100">
                    <p className="text-sm font-medium text-slate-900">{user?.displayName}</p>
                    <p className="text-xs text-slate-500 truncate">{user?.email}</p>
                  </div>
                  <button
                    onClick={openLogoutModal}
                    className="w-full px-4 py-2 text-left text-sm text-violet-600
                      hover:bg-violet-50 flex items-center gap-2"
                  >
                    <LogOut size={16} />
                    {tLocal("Sign Out")}
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </motion.div>

      {/* Floating Expand Button - Only visible when fully collapsed */}
      <AnimatePresence>
        {isFullyCollapsed && (
          <motion.button
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setIsFullyCollapsed(false)}
            className="hidden md:flex fixed top-4 left-4 z-50 p-3 bg-violet-600 text-white rounded-full shadow-lg hover:bg-violet-700 transition-colors items-center justify-center"
            title="Show sidebar"
          >
            <PanelLeftOpen size={20} />
          </motion.button>
        )}
      </AnimatePresence>
    </>
  );
};

export default DesktopNavigation;


// import React from 'react';
// import { useNavigate } from 'react-router-dom';
// import { motion, AnimatePresence } from 'framer-motion';
// import { 
//   Sparkles, Home, Users, Settings, 
//   HelpCircle, LogOut, Menu, MessageCircle, Globe, DatabaseZap, Volleyball, Activity,
//   Info, Zap, Swords // Added Swords icon for MMA Fights
// } from 'lucide-react';
// import { useLanguage } from '../../services/languageContext';


// const DesktopNavigation = ({ 
//   user, 
//   isExpanded, 
//   setIsExpanded,
//   // showBackToChat,
//   showUserMenu,
//   setShowUserMenu,
//   openLogoutModal,
//   dropdownRef,
//   t 
// }) => {
//   const navigate = useNavigate();

//   const handleNavigation = (path) => {
//     navigate(path);
//     setShowUserMenu(false);
//   };

//   const { language, changeLanguage, supportedLanguages } = useLanguage();


//   return (
//     <motion.div 
//       ref={dropdownRef}
//       animate={{ width: isExpanded ? 240 : 72 }}
//       transition={{ duration: 0.15, ease: [0.4, 0.0, 0.2, 1] }}
//       className="hidden md:block fixed top-0 left-0 h-screen bg-slate-50 shadow-lg z-50 overflow-hidden"
//     >
//       <div className="flex flex-col h-full">
//         {/* Hamburger Menu */}
//         <button
//           onClick={() => setIsExpanded(!isExpanded)}
//           className="w-full h-14 flex items-center px-6 text-slate-700 hover:bg-slate-100 transition-colors"
//         >
//           <Menu size={22} className="flex-shrink-0 text-black-600" />
//           <motion.span
//             initial={false}
//             animate={{ opacity: isExpanded ? 1 : 0 }}
//             transition={{ duration: 0.1 }}
//             className="ml-4 text-lg font-sans font-bold whitespace-nowrap"
//           >
//             PickStar
//           </motion.span>
//         </button>

//         <div className="flex-1 px-3 pt-3 space-y-0.5">
//           {/* {showBackToChat && (
//             <button
//               onClick={() => navigate('/', { state: { returnToChat: true } })}
//               className="w-full h-11 flex items-center px-3 rounded-lg text-emerald-600 hover:bg-emerald-50 transition-colors"
//             >
//               <MessageCircle size={22} className="flex-shrink-0 text-violet-600" />
//               <motion.span
//                 initial={false}
//                 animate={{ opacity: isExpanded ? 1 : 0 }}
//                 transition={{ duration: 0.1 }}
//                 className="ml-4 text-sm font-medium whitespace-nowrap"
//               >
//                 Go to Chat
//               </motion.span>
//             </button>
//           )} */}
          
//           {[
//             { icon: Home, label: t('navigation.home'), path: '/' },
//             // { icon: Users, label: t('navigation.community'), path: '/community' },
//             { icon: Settings, label: t('navigation.settings'), path: '/settings' },
//             { icon: Sparkles, label: t('navigation.upgrade'), path: '/upgrade', isUpgrade: true },
//             { icon: HelpCircle, label: t('navigation.help'), path: '/help' },
//             { icon: Zap, label: 'About Us', path: '/about-us' } // Added About Us navigation item
//           ].map((item) => (
//             <button
//               key={item.path}
//               onClick={() => handleNavigation(item.path)}
//               className={`w-full h-11 flex items-center px-3 rounded-lg ${
//                 item.isUpgrade ? 'text-emerald-500' : 'text-violet-600'
//               } hover:bg-slate-50 transition-colors`}
//             >
//               <item.icon size={22} className="flex-shrink-0" />
//               <motion.span
//                 initial={false}
//                 animate={{ opacity: isExpanded ? 1 : 0 }}
//                 transition={{ duration: 0.1 }}
//                 className="ml-4 text-sm font-medium whitespace-nowrap"
//               >
//                 {item.label}
//               </motion.span>
//             </button>
//           ))}

//           {/* Archived Chat History Button */}
//           <button
//             onClick={() => handleNavigation('/chat-history')}
//             className="w-full h-11 flex items-center px-3 rounded-lg text-violet-600 hover:bg-violet-50 transition-colors"
//           >
//             <DatabaseZap size={22} className="flex-shrink-0 text-violet-600" />
//             <motion.span
//               initial={false}
//               animate={{ opacity: isExpanded ? 1 : 0 }}
//               transition={{ duration: 0.1 }}
//               className="ml-4 text-sm font-medium whitespace-nowrap"
//             >
//               Chat Archive
//             </motion.span>
//           </button>


//           {/* MMA Fights Button - Added at the bottom */}
//           {/* <button
//             onClick={() => handleNavigation('/mma-fights')}
//             className="w-full h-11 flex items-center px-3 rounded-lg text-violet-600 hover:bg-violet-50 transition-colors"
//           >
//             <Swords size={22} className="flex-shrink-0 text-violet-600" />
//             <motion.span
//               initial={false}
//               animate={{ opacity: isExpanded ? 1 : 0 }}
//               transition={{ duration: 0.1 }}
//               className="ml-4 text-sm font-medium whitespace-nowrap"
//             >
//               MMA Fights
//             </motion.span>
//           </button> */}

//                     {/* Soccer Button - Added at the bottom */}
//           {/* <button
//             onClick={() => handleNavigation('/football')}
//             className="w-full h-11 flex items-center px-3 rounded-lg text-violet-600 hover:bg-violet-50 transition-colors"
//           >
//             <Globe size={22} className="flex-shrink-0 text-violet-600" />
//             <motion.span
//               initial={false}
//               animate={{ opacity: isExpanded ? 1 : 0 }}
//               transition={{ duration: 0.1 }}
//               className="ml-4 text-sm font-medium whitespace-nowrap"
//             >
//               Football
//             </motion.span>
//           </button> */}

//         {/* Volleyball Button - Added at the bottom */}
//           {/* <button
//             onClick={() => handleNavigation('/volleyball')}
//             className="w-full h-11 flex items-center px-3 rounded-lg text-violet-600 hover:bg-violet-50 transition-colors"
//           >
//             <Volleyball size={22} className="flex-shrink-0 text-violet-600" />
//             <motion.span
//               initial={false}
//               animate={{ opacity: isExpanded ? 1 : 0 }}
//               transition={{ duration: 0.1 }}
//               className="ml-4 text-sm font-medium whitespace-nowrap"
//             >
//               Volleyball
//             </motion.span>
//           </button> */}

//           {/* NFL Button - Added at the bottom */}
//           {/* <button
//             onClick={() => handleNavigation('/nfl')}
//             className="w-full h-11 flex items-center px-3 rounded-lg text-violet-600 hover:bg-violet-50 transition-colors"
//           >
//             <FlagTriangleRight size={22} className="flex-shrink-0 text-violet-600" />
//             <motion.span
//               initial={false}
//               animate={{ opacity: isExpanded ? 1 : 0 }}
//               transition={{ duration: 0.1 }}
//               className="ml-4 text-sm font-medium whitespace-nowrap"
//             >
//               NFL
//             </motion.span>
//           </button> */}


//           <button
//             onClick={openLogoutModal}
//             className="w-full h-11 flex items-center px-3 rounded-lg text-violet-600 hover:bg-red-50 transition-colors mt-2"
//           >
//             <LogOut size={22} className="flex-shrink-0 text-violet-600" />
//             <motion.span
//               initial={false}
//               animate={{ opacity: isExpanded ? 1 : 0 }}
//               transition={{ duration: 0.1 }}
//               className="ml-4 text-sm font-medium whitespace-nowrap"
//             >
//               {t('navigation.signOut')}
//             </motion.span>
//           </button>
//         </div>

        

//         {/* User Menu */}
//         <div className="p-3 mt-auto">
//           <button 
//             onClick={() => setShowUserMenu(!showUserMenu)}
//             className={`flex items-center ${
//               isExpanded ? 'w-full px-3' : 'w-10 mx-auto'
//             } h-10 bg-amber-50 hover:bg-amber-100 text-slate-700 
//               rounded-lg transition-all duration-150`}
//           >
//             <div className={`${!isExpanded && 'w-full'} flex justify-center text-base font-semibold`}>
//               {user?.displayName?.charAt(0).toUpperCase()}
//             </div>
//             <motion.span
//               initial={false}
//               animate={{ opacity: isExpanded ? 1 : 0 }}
//               transition={{ duration: 0.1 }}
//               className="ml-3 text-sm font-medium truncate whitespace-nowrap"
//             >
//               {user?.displayName}
//             </motion.span>
//           </button>

//           <AnimatePresence>
//             {showUserMenu && (
//               <motion.div
//                 initial={{ opacity: 0, y: 8 }}
//                 animate={{ opacity: 1, y: 0 }}
//                 exit={{ opacity: 0, y: 8 }}
//                 transition={{ duration: 0.1 }}
//                 className={`absolute bottom-[72px] ${
//                   isExpanded ? 'left-3 right-3' : 'left-16 w-48'
//                 } bg-white rounded-lg shadow-lg py-2 border border-slate-200`}
//               >
//                 <div className="px-4 py-2 border-b border-slate-100">
//                   <p className="text-sm font-medium text-slate-900">{user?.displayName}</p>
//                   <p className="text-xs text-slate-500 truncate">{user?.email}</p>
//                 </div>
//                 <button
//                   onClick={openLogoutModal}
//                   className="w-full px-4 py-2 text-left text-sm text-violet-600
//                     hover:bg-violet-50 flex items-center gap-2"
//                 >
//                   <LogOut size={16} />
//                   {tLocal("Sign Out")}
//                 </button>
//               </motion.div>
//             )}
//           </AnimatePresence>
//         </div>
//       </div>
//     </motion.div>
//   );
// };

// export default DesktopNavigation; 


// import React from 'react';
// import { useNavigate } from 'react-router-dom';
// import { motion, AnimatePresence } from 'framer-motion';
// import { 
//   Sparkles, Home, Users, Settings, 
//   HelpCircle, LogOut, Menu, MessageCircle, Globe, DatabaseZap,
//   Info, Zap // Added Info icon for About Us
// } from 'lucide-react';
// import { useLanguage } from '../../services/languageContext';


// const DesktopNavigation = ({ 
//   user, 
//   isExpanded, 
//   setIsExpanded,
//   // showBackToChat,
//   showUserMenu,
//   setShowUserMenu,
//   openLogoutModal,
//   dropdownRef,
//   t 
// }) => {
//   const navigate = useNavigate();

//   const handleNavigation = (path) => {
//     navigate(path);
//     setShowUserMenu(false);
//   };

//   const { language, changeLanguage, supportedLanguages } = useLanguage();


//   return (
//     <motion.div 
//       ref={dropdownRef}
//       animate={{ width: isExpanded ? 240 : 72 }}
//       transition={{ duration: 0.15, ease: [0.4, 0.0, 0.2, 1] }}
//       className="hidden md:block fixed top-0 left-0 h-screen bg-slate-50 shadow-lg z-50 overflow-hidden"
//     >
//       <div className="flex flex-col h-full">
//         {/* Hamburger Menu */}
//         <button
//           onClick={() => setIsExpanded(!isExpanded)}
//           className="w-full h-14 flex items-center px-6 text-slate-700 hover:bg-slate-100 transition-colors"
//         >
//           <Menu size={22} className="flex-shrink-0 text-black-600" />
//           <motion.span
//             initial={false}
//             animate={{ opacity: isExpanded ? 1 : 0 }}
//             transition={{ duration: 0.1 }}
//             className="ml-4 text-lg font-sans font-bold whitespace-nowrap"
//           >
//             PickStar
//           </motion.span>
//         </button>

//         <div className="flex-1 px-3 pt-3 space-y-0.5">
//           {/* {showBackToChat && (
//             <button
//               onClick={() => navigate('/', { state: { returnToChat: true } })}
//               className="w-full h-11 flex items-center px-3 rounded-lg text-emerald-600 hover:bg-emerald-50 transition-colors"
//             >
//               <MessageCircle size={22} className="flex-shrink-0 text-violet-600" />
//               <motion.span
//                 initial={false}
//                 animate={{ opacity: isExpanded ? 1 : 0 }}
//                 transition={{ duration: 0.1 }}
//                 className="ml-4 text-sm font-medium whitespace-nowrap"
//               >
//                 Go to Chat
//               </motion.span>
//             </button>
//           )} */}
          
//           {[
//             { icon: Home, label: t('navigation.home'), path: '/' },
//             // { icon: Users, label: t('navigation.community'), path: '/community' },
//             { icon: Settings, label: t('navigation.settings'), path: '/settings' },
//             { icon: Sparkles, label: t('navigation.upgrade'), path: '/upgrade', isUpgrade: true },
//             { icon: HelpCircle, label: t('navigation.help'), path: '/help' },
//             { icon: Zap, label: 'About Us', path: '/about-us' } // Added About Us navigation item
//           ].map((item) => (
//             <button
//               key={item.path}
//               onClick={() => handleNavigation(item.path)}
//               className={`w-full h-11 flex items-center px-3 rounded-lg ${
//                 item.isUpgrade ? 'text-emerald-500' : 'text-violet-600'
//               } hover:bg-slate-50 transition-colors`}
//             >
//               <item.icon size={22} className="flex-shrink-0" />
//               <motion.span
//                 initial={false}
//                 animate={{ opacity: isExpanded ? 1 : 0 }}
//                 transition={{ duration: 0.1 }}
//                 className="ml-4 text-sm font-medium whitespace-nowrap"
//               >
//                 {item.label}
//               </motion.span>
//             </button>
//           ))}

//           {/* Archived Chat History Button */}
//           <button
//             onClick={() => handleNavigation('/chat-history')}
//             className="w-full h-11 flex items-center px-3 rounded-lg text-violet-600 hover:bg-violet-50 transition-colors"
//           >
//             <DatabaseZap size={22} className="flex-shrink-0 text-violet-600" />
//             <motion.span
//               initial={false}
//               animate={{ opacity: isExpanded ? 1 : 0 }}
//               transition={{ duration: 0.1 }}
//               className="ml-4 text-sm font-medium whitespace-nowrap"
//             >
//               Chat Archive
//             </motion.span>
//           </button>


//           <button
//             onClick={openLogoutModal}
//             className="w-full h-11 flex items-center px-3 rounded-lg text-violet-600 hover:bg-red-50 transition-colors mt-2"
//           >
//             <LogOut size={22} className="flex-shrink-0 text-violet-600" />
//             <motion.span
//               initial={false}
//               animate={{ opacity: isExpanded ? 1 : 0 }}
//               transition={{ duration: 0.1 }}
//               className="ml-4 text-sm font-medium whitespace-nowrap"
//             >
//               {t('navigation.signOut')}
//             </motion.span>
//           </button>
//         </div>

//         {/* User Menu */}
//         <div className="p-3 mt-auto">
//           <button 
//             onClick={() => setShowUserMenu(!showUserMenu)}
//             className={`flex items-center ${
//               isExpanded ? 'w-full px-3' : 'w-10 mx-auto'
//             } h-10 bg-amber-50 hover:bg-amber-100 text-slate-700 
//               rounded-lg transition-all duration-150`}
//           >
//             <div className={`${!isExpanded && 'w-full'} flex justify-center text-base font-semibold`}>
//               {user?.displayName?.charAt(0).toUpperCase()}
//             </div>
//             <motion.span
//               initial={false}
//               animate={{ opacity: isExpanded ? 1 : 0 }}
//               transition={{ duration: 0.1 }}
//               className="ml-3 text-sm font-medium truncate whitespace-nowrap"
//             >
//               {user?.displayName}
//             </motion.span>
//           </button>

//           <AnimatePresence>
//             {showUserMenu && (
//               <motion.div
//                 initial={{ opacity: 0, y: 8 }}
//                 animate={{ opacity: 1, y: 0 }}
//                 exit={{ opacity: 0, y: 8 }}
//                 transition={{ duration: 0.1 }}
//                 className={`absolute bottom-[72px] ${
//                   isExpanded ? 'left-3 right-3' : 'left-16 w-48'
//                 } bg-white rounded-lg shadow-lg py-2 border border-slate-200`}
//               >
//                 <div className="px-4 py-2 border-b border-slate-100">
//                   <p className="text-sm font-medium text-slate-900">{user?.displayName}</p>
//                   <p className="text-xs text-slate-500 truncate">{user?.email}</p>
//                 </div>
//                 <button
//                   onClick={openLogoutModal}
//                   className="w-full px-4 py-2 text-left text-sm text-violet-600
//                     hover:bg-violet-50 flex items-center gap-2"
//                 >
//                   <LogOut size={16} />
//                   {tLocal("Sign Out")}
//                 </button>
//               </motion.div>
//             )}
//           </AnimatePresence>
//         </div>
//       </div>
//     </motion.div>
//   );
// };

// export default DesktopNavigation;



// // works with dropdownmenu.js
// import React from 'react';
// import { useNavigate } from 'react-router-dom';
// import { motion, AnimatePresence } from 'framer-motion';
// import { 
//   Sparkles, Home, Users, Settings, 
//   HelpCircle, LogOut, Menu, MessageCircle, Globe, DatabaseZap
// } from 'lucide-react';
// import { useLanguage } from '../../services/languageContext';


// const DesktopNavigation = ({ 
//   user, 
//   isExpanded, 
//   setIsExpanded,
//   // showBackToChat,
//   showUserMenu,
//   setShowUserMenu,
//   openLogoutModal,
//   dropdownRef,
//   t 
// }) => {
//   const navigate = useNavigate();

//   const handleNavigation = (path) => {
//     navigate(path);
//     setShowUserMenu(false);
//   };

//   const { language, changeLanguage, supportedLanguages } = useLanguage();


//   return (
//     <motion.div 
//       ref={dropdownRef}
//       animate={{ width: isExpanded ? 240 : 72 }}
//       transition={{ duration: 0.15, ease: [0.4, 0.0, 0.2, 1] }}
//       className="hidden md:block fixed top-0 left-0 h-screen bg-slate-50 shadow-lg z-50 overflow-hidden"
//     >
//       <div className="flex flex-col h-full">
//         {/* Hamburger Menu */}
//         <button
//           onClick={() => setIsExpanded(!isExpanded)}
//           className="w-full h-14 flex items-center px-6 text-slate-700 hover:bg-slate-100 transition-colors"
//         >
//           <Menu size={22} className="flex-shrink-0 text-black-600" />
//           <motion.span
//             initial={false}
//             animate={{ opacity: isExpanded ? 1 : 0 }}
//             transition={{ duration: 0.1 }}
//             className="ml-4 text-lg font-sans font-bold whitespace-nowrap"
//           >
//             PickStar
//           </motion.span>
//         </button>

//         <div className="flex-1 px-3 pt-3 space-y-0.5">
//           {/* {showBackToChat && (
//             <button
//               onClick={() => navigate('/', { state: { returnToChat: true } })}
//               className="w-full h-11 flex items-center px-3 rounded-lg text-emerald-600 hover:bg-emerald-50 transition-colors"
//             >
//               <MessageCircle size={22} className="flex-shrink-0 text-violet-600" />
//               <motion.span
//                 initial={false}
//                 animate={{ opacity: isExpanded ? 1 : 0 }}
//                 transition={{ duration: 0.1 }}
//                 className="ml-4 text-sm font-medium whitespace-nowrap"
//               >
//                 Go to Chat
//               </motion.span>
//             </button>
//           )} */}
          
//           {/* Chat History Button */}
//           <button
//             onClick={() => handleNavigation('/chat-history')}
//             className="w-full h-11 flex items-center px-3 rounded-lg text-violet-600 hover:bg-violet-50 transition-colors"
//           >
//             <DatabaseZap size={22} className="flex-shrink-0 text-violet-600" />
//             <motion.span
//               initial={false}
//               animate={{ opacity: isExpanded ? 1 : 0 }}
//               transition={{ duration: 0.1 }}
//               className="ml-4 text-sm font-medium whitespace-nowrap"
//             >
//               Chat History
//             </motion.span>
//           </button>
          
//           {[
//             { icon: Home, label: t('navigation.home'), path: '/' },
//             // { icon: Users, label: t('navigation.community'), path: '/community' },
//             { icon: Settings, label: t('navigation.settings'), path: '/settings' },
//             { icon: Sparkles, label: t('navigation.upgrade'), path: '/upgrade', isUpgrade: true },
//             { icon: HelpCircle, label: t('navigation.help'), path: '/help' }
//           ].map((item) => (
//             <button
//               key={item.path}
//               onClick={() => handleNavigation(item.path)}
//               className={`w-full h-11 flex items-center px-3 rounded-lg ${
//                 item.isUpgrade ? 'text-emerald-500' : 'text-violet-600'
//               } hover:bg-slate-50 transition-colors`}
//             >
//               <item.icon size={22} className="flex-shrink-0" />
//               <motion.span
//                 initial={false}
//                 animate={{ opacity: isExpanded ? 1 : 0 }}
//                 transition={{ duration: 0.1 }}
//                 className="ml-4 text-sm font-medium whitespace-nowrap"
//               >
//                 {item.label}
//               </motion.span>
//             </button>
//           ))}

//           <button
//             onClick={openLogoutModal}
//             className="w-full h-11 flex items-center px-3 rounded-lg text-violet-600 hover:bg-red-50 transition-colors mt-2"
//           >
//             <LogOut size={22} className="flex-shrink-0 text-violet-600" />
//             <motion.span
//               initial={false}
//               animate={{ opacity: isExpanded ? 1 : 0 }}
//               transition={{ duration: 0.1 }}
//               className="ml-4 text-sm font-medium whitespace-nowrap"
//             >
//               {t('navigation.signOut')}
//             </motion.span>
//           </button>
//         </div>

//         {/* User Menu */}
//         <div className="p-3 mt-auto">
//           <button 
//             onClick={() => setShowUserMenu(!showUserMenu)}
//             className={`flex items-center ${
//               isExpanded ? 'w-full px-3' : 'w-10 mx-auto'
//             } h-10 bg-amber-50 hover:bg-amber-100 text-slate-700 
//               rounded-lg transition-all duration-150`}
//           >
//             <div className={`${!isExpanded && 'w-full'} flex justify-center text-base font-semibold`}>
//               {user?.displayName?.charAt(0).toUpperCase()}
//             </div>
//             <motion.span
//               initial={false}
//               animate={{ opacity: isExpanded ? 1 : 0 }}
//               transition={{ duration: 0.1 }}
//               className="ml-3 text-sm font-medium truncate whitespace-nowrap"
//             >
//               {user?.displayName}
//             </motion.span>
//           </button>

//           <AnimatePresence>
//             {showUserMenu && (
//               <motion.div
//                 initial={{ opacity: 0, y: 8 }}
//                 animate={{ opacity: 1, y: 0 }}
//                 exit={{ opacity: 0, y: 8 }}
//                 transition={{ duration: 0.1 }}
//                 className={`absolute bottom-[72px] ${
//                   isExpanded ? 'left-3 right-3' : 'left-16 w-48'
//                 } bg-white rounded-lg shadow-lg py-2 border border-slate-200`}
//               >
//                 <div className="px-4 py-2 border-b border-slate-100">
//                   <p className="text-sm font-medium text-slate-900">{user?.displayName}</p>
//                   <p className="text-xs text-slate-500 truncate">{user?.email}</p>
//                 </div>
//                 <button
//                   onClick={openLogoutModal}
//                   className="w-full px-4 py-2 text-left text-sm text-violet-600
//                     hover:bg-violet-50 flex items-center gap-2"
//                 >
//                   <LogOut size={16} />
//                   {tLocal("Sign Out")}
//                 </button>
//               </motion.div>
//             )}
//           </AnimatePresence>
//         </div>
//       </div>
//     </motion.div>
//   );
// };

// export default DesktopNavigation;


// import React from 'react';
// import { useNavigate } from 'react-router-dom';
// import { motion, AnimatePresence } from 'framer-motion';
// import { 
//   Sparkles, Home, Users, Settings, 
//   HelpCircle, LogOut, Menu, MessageCircle , Globe
// } from 'lucide-react';
// import { useLanguage } from '../../services/languageContext';


// const DesktopNavigation = ({ 
//   user, 
//   isExpanded, 
//   setIsExpanded,
//   showBackToChat,
//   showUserMenu,
//   setShowUserMenu,
//   openLogoutModal,
//   dropdownRef,
//   t 
// }) => {
//   const navigate = useNavigate();

//   const handleNavigation = (path) => {
//     navigate(path);
//     setShowUserMenu(false);
//   };

//   const { language, changeLanguage, supportedLanguages } = useLanguage();


//   return (
//     <motion.div 
//       ref={dropdownRef}
//       animate={{ width: isExpanded ? 240 : 72 }}
//       transition={{ duration: 0.15, ease: [0.4, 0.0, 0.2, 1] }}
//       className="hidden md:block fixed top-0 left-0 h-screen bg-white shadow-lg z-50 overflow-hidden"
//     >
//       <div className="flex flex-col h-full">
//         {/* Hamburger Menu */}
//         <button
//           onClick={() => setIsExpanded(!isExpanded)}
//           className="w-full h-14 flex items-center px-6 text-slate-700 hover:bg-slate-100 transition-colors"
//         >
//           <Menu size={22} className="flex-shrink-0 text-slate-700" />
//           <motion.span
//             initial={false}
//             animate={{ opacity: isExpanded ? 1 : 0 }}
//             transition={{ duration: 0.1 }}
//             className="ml-4 text-lg font-semibold whitespace-nowrap"
//           >
//             PickStar
//           </motion.span>
//         </button>
//             {/* NEW: Language Selector placed right after the Hamburger */}
//             {/* <div className="p-2 mx-3 my-2 bg-white/10 rounded-full flex items-center gap-2">
//               {supportedLanguages.map((lng) => (
//                 <button
//                   key={lng}
//                   onClick={() => changeLanguage(lng)}
//                   className={`flex items-center px-3 py-1 text-sm font-medium rounded-full transition-colors ${
//                     language === lng
//                       ? 'bg-emerald-300 text-emerald-900'
//                       : 'text-white hover:bg-white/20'
//                   }`}
//                 >
//                   <Globe size={16} className="mr-1" />
//                   {lng.toUpperCase()}
//                 </button>
//               ))}
//             </div> */}
//         <div className="flex-1 px-3 pt-3 space-y-0.5">
//           {showBackToChat && (
//             <button
//               onClick={() => navigate('/', { state: { returnToChat: true } })}
//               className="w-full h-11 flex items-center px-3 rounded-lg text-emerald-600 hover:bg-emerald-50 transition-colors"
//             >
//               <MessageCircle size={22} className="flex-shrink-0" />
//               <motion.span
//                 initial={false}
//                 animate={{ opacity: isExpanded ? 1 : 0 }}
//                 transition={{ duration: 0.1 }}
//                 className="ml-4 text-sm font-medium whitespace-nowrap"
//               >
//                 Go to Chat
//               </motion.span>
//             </button>
//           )}
          
//           {[
//             { icon: Home, label: t('navigation.home'), path: '/' },
//             { icon: Users, label: t('navigation.community'), path: '/community' },
//             { icon: Settings, label: t('navigation.settings'), path: '/settings' },
//             { icon: Sparkles, label: t('navigation.upgrade'), path: '/upgrade', isUpgrade: true },
//             { icon: HelpCircle, label: t('navigation.help'), path: '/help' }
//           ].map((item) => (
//             <button
//               key={item.path}
//               onClick={() => handleNavigation(item.path)}
//               className={`w-full h-11 flex items-center px-3 rounded-lg ${
//                 item.isUpgrade ? 'text-emerald-500' : 'text-slate-700'
//               } hover:bg-slate-50 transition-colors`}
//             >
//               <item.icon size={22} className="flex-shrink-0" />
//               <motion.span
//                 initial={false}
//                 animate={{ opacity: isExpanded ? 1 : 0 }}
//                 transition={{ duration: 0.1 }}
//                 className="ml-4 text-sm font-medium whitespace-nowrap"
//               >
//                 {item.label}
//               </motion.span>
//             </button>
//           ))}

//           <button
//             onClick={openLogoutModal}
//             className="w-full h-11 flex items-center px-3 rounded-lg text-red-500 hover:bg-red-50 transition-colors mt-2"
//           >
//             <LogOut size={22} className="flex-shrink-0" />
//             <motion.span
//               initial={false}
//               animate={{ opacity: isExpanded ? 1 : 0 }}
//               transition={{ duration: 0.1 }}
//               className="ml-4 text-sm font-medium whitespace-nowrap"
//             >
//               {t('navigation.signOut')}
//             </motion.span>
//           </button>
//         </div>

//         {/* User Menu */}
//         <div className="p-3 mt-auto">
//           <button 
//             onClick={() => setShowUserMenu(!showUserMenu)}
//             className={`flex items-center ${
//               isExpanded ? 'w-full px-3' : 'w-10 mx-auto'
//             } h-10 bg-amber-50 hover:bg-amber-100 text-slate-700 
//               rounded-lg transition-all duration-150`}
//           >
//             <div className={`${!isExpanded && 'w-full'} flex justify-center text-base font-semibold`}>
//               {user?.displayName?.charAt(0).toUpperCase()}
//             </div>
//             <motion.span
//               initial={false}
//               animate={{ opacity: isExpanded ? 1 : 0 }}
//               transition={{ duration: 0.1 }}
//               className="ml-3 text-sm font-medium truncate whitespace-nowrap"
//             >
//               {user?.displayName}
//             </motion.span>
//           </button>

//           <AnimatePresence>
//             {showUserMenu && (
//               <motion.div
//                 initial={{ opacity: 0, y: 8 }}
//                 animate={{ opacity: 1, y: 0 }}
//                 exit={{ opacity: 0, y: 8 }}
//                 transition={{ duration: 0.1 }}
//                 className={`absolute bottom-[72px] ${
//                   isExpanded ? 'left-3 right-3' : 'left-16 w-48'
//                 } bg-white rounded-lg shadow-lg py-2 border border-slate-200`}
//               >
//                 <div className="px-4 py-2 border-b border-slate-100">
//                   <p className="text-sm font-medium text-slate-900">{user?.displayName}</p>
//                   <p className="text-xs text-slate-500 truncate">{user?.email}</p>
//                 </div>
//                 <button
//                   onClick={openLogoutModal}
//                   className="w-full px-4 py-2 text-left text-sm text-red-500 
//                     hover:bg-red-50 flex items-center gap-2"
//                 >
//                   <LogOut size={16} />
//                   {tLocal("Sign Out")}
//                 </button>
//               </motion.div>
//             )}
//           </AnimatePresence>
//         </div>
//       </div>
//     </motion.div>
//   );
// };

// export default DesktopNavigation;